import { session } from "@hotwired/turbo"

/**
 * The only supported way to write browser history in this app.
 *
 * Turbo acts on popstate only for entries carrying `state.turbo`, and it keeps
 * its own index of the history stack to tell Back from Forward. A raw
 * `history.pushState` writes neither, which breaks restoration for that entry
 * and desynchronises the index for every later navigation.
 *
 * `session.history` is Turbo internal API, pinned to @hotwired/turbo 8.
 */

const write = (method, href) => {
  const url = new URL(href, window.location.href)

  // A replace overwrites the current entry, so it has to keep that entry's
  // restoration identifier, so the restoration entry is not orphaned.
  const identifier = method === "replace" ? session.history.restorationIdentifier : undefined

  session.history[method](url, identifier)

  // Turbo records a scroll position only from a real scroll event, so a pushed
  // entry is born without one, and a later Back/Forward onto it falls through to
  // scrolling at the URL's anchor instead. Nothing scrolled, so the position the
  // entry was written at is the one to come back to.
  session.history.updateRestorationData({
    scrollPosition: { x: window.scrollX, y: window.scrollY },
  })

  // Turbo decides whether a later Back/Forward has to re-render by comparing the
  // target against the last *rendered* location, which only moves when something
  // renders. We change the URL without rendering, so without this the comparison
  // drifts further from the address bar with every write, and identical Back
  // presses start behaving differently - sometimes a silent scroll, sometimes a
  // full restore visit with a progress bar. Nothing rendered, but this URL is
  // what is on screen.
  session.view.lastRenderedLocation = url
}

/** New history entry. For navigation the user should be able to go Back from. */
export const pushUrl = (href) => write("push", href)

/** Overwrite the current entry. For state that is not navigation. */
export const replaceUrl = (href) => write("replace", href)
